import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Clock, Mail, Phone, Linkedin, Instagram, Facebook, Twitter } from "lucide-react";
import { LOGO_MARK_URL, BUSINESS, SERVICES } from "@/data/content";
import { api } from "@/lib/api";

export default function Footer() {
    const [settings, setSettings] = useState(null);
    useEffect(() => {
        api.get("/settings/public").then((r) => setSettings(r.data)).catch(() => {});
    }, []);
    const socials = settings?.socials || {};

    return (
        <footer className="border-t border-white/10 bg-[#080B11]" data-testid="site-footer">
            <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16 py-16 grid gap-12 md:grid-cols-2 lg:grid-cols-4">
                <div>
                    <div className="flex items-center gap-4">
                        <img src={LOGO_MARK_URL} alt="Innovative Fusion Hives logo" className="h-16 w-16 rounded-2xl object-cover ring-1 ring-white/20 shadow-[0_4px_20px_rgba(245,158,11,0.12)]" />
                        <div>
                            <p className="font-display font-extrabold text-lg text-white leading-tight">Innovative<br />Fusion Hives</p>
                        </div>
                    </div>
                    <p className="mt-4 font-mono2 text-[11px] uppercase tracking-[0.25em] text-amber-400">{BUSINESS.motto}</p>
                    <p className="mt-4 text-sm text-slate-400 leading-relaxed">
                        A full-service digital creative agency helping businesses build distinctive brands and grow online.
                    </p>
                    <div className="mt-5 flex gap-3">
                        {socials.linkedin && <a href={socials.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" data-testid="footer-social-linkedin" className="p-2 rounded-lg border border-white/10 text-slate-400 hover:text-white hover:border-violet-500/50 transition-colors"><Linkedin className="h-4 w-4" /></a>}
                        {socials.instagram && <a href={socials.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="footer-social-instagram" className="p-2 rounded-lg border border-white/10 text-slate-400 hover:text-white hover:border-violet-500/50 transition-colors"><Instagram className="h-4 w-4" /></a>}
                        {socials.facebook && <a href={socials.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" data-testid="footer-social-facebook" className="p-2 rounded-lg border border-white/10 text-slate-400 hover:text-white hover:border-violet-500/50 transition-colors"><Facebook className="h-4 w-4" /></a>}
                        {socials.x && <a href={socials.x} target="_blank" rel="noopener noreferrer" aria-label="X" data-testid="footer-social-x" className="p-2 rounded-lg border border-white/10 text-slate-400 hover:text-white hover:border-violet-500/50 transition-colors"><Twitter className="h-4 w-4" /></a>}
                    </div>
                </div>
                <div>
                    <h3 className="font-display font-semibold text-white text-sm uppercase tracking-wider">Services</h3>
                    <ul className="mt-4 space-y-2.5">
                        {SERVICES.map((s) => (
                            <li key={s.slug}>
                                <Link to={`/services/${s.slug}`} data-testid={`footer-service-${s.slug}`} className="text-sm text-slate-400 hover:text-violet-300 transition-colors">{s.name}</Link>
                            </li>
                        ))}
                    </ul>
                </div>
                <div>
                    <h3 className="font-display font-semibold text-white text-sm uppercase tracking-wider">Company</h3>
                    <ul className="mt-4 space-y-2.5">
                        <li><Link to="/about" data-testid="footer-link-about" className="text-sm text-slate-400 hover:text-violet-300 transition-colors">About Us</Link></li>
                        <li><Link to="/portfolio" data-testid="footer-link-portfolio" className="text-sm text-slate-400 hover:text-violet-300 transition-colors">Our Work</Link></li>
                        <li><Link to="/process" data-testid="footer-link-process" className="text-sm text-slate-400 hover:text-violet-300 transition-colors">Our Process</Link></li>
                        <li><Link to="/contact" data-testid="footer-link-contact" className="text-sm text-slate-400 hover:text-violet-300 transition-colors">Contact</Link></li>
                    </ul>
                </div>
                <div>
                    <h3 className="font-display font-semibold text-white text-sm uppercase tracking-wider">Visit Us</h3>
                    <ul className="mt-4 space-y-3 text-sm text-slate-400">
                        <li className="flex gap-2.5">
                            <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-violet-400" />
                            <a href={BUSINESS.mapsUrl} target="_blank" rel="noopener noreferrer" data-testid="footer-address-link" className="hover:text-white transition-colors">
                                {BUSINESS.address1}<br />{BUSINESS.address2}
                            </a>
                        </li>
                        <li className="flex gap-2.5">
                            <Clock className="h-4 w-4 mt-0.5 shrink-0 text-violet-400" />
                            <span>{BUSINESS.hours}</span>
                        </li>
                        <li className="flex gap-2.5">
                            <Mail className="h-4 w-4 mt-0.5 shrink-0 text-violet-400" />
                            {settings?.contact_email
                                ? <a href={`mailto:${settings.contact_email}`} data-testid="footer-email-link" className="hover:text-white transition-colors">{settings.contact_email}</a>
                                : <span className="text-slate-500 italic">Email — use the inquiry form</span>}
                        </li>
                        <li className="flex gap-2.5">
                            <Phone className="h-4 w-4 mt-0.5 shrink-0 text-violet-400" />
                            {settings?.phone
                                ? <a href={`tel:${settings.phone.replace(/[^+\d]/g, "")}`} data-testid="footer-phone-link" className="hover:text-white transition-colors">{settings.phone}</a>
                                : <span className="text-slate-500 italic">Phone — coming soon</span>}
                        </li>
                    </ul>
                </div>
            </div>
            <div className="border-t border-white/5">
                <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
                    <p className="text-xs text-slate-500">© {new Date().getFullYear()} Innovative Fusion Hives. All rights reserved.</p>
                    <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-600">Digital Agency — Long Beach, California</p>
                </div>
            </div>
        </footer>
    );
}
